import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  Typography,
  Select,
  MenuItem,
  TextField,
  TextareaAutosize,
  IconButton,
  CircularProgress,
  Divider
} from '@mui/material';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import ArticleIcon from '@mui/icons-material/Article';
import LanguageIcon from '@mui/icons-material/Language';
import SmartDisplayIcon from '@mui/icons-material/SmartDisplay';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useGetItemQuery, useUpdatePropertyMutation } from '../store/docApi';
import { generateUniqueId } from '../utils';
import ProjectBreadcrumbs from '../Project/ProjectBreadcrumbs';

const Bibliography = (props) => {
  const params = useParams();
  // Prefer item from props (for WorkspaceEditor), fallback to query for project
  const { data: queriedItem, isLoading } = useGetItemQuery({ label: 'project', pk: params.pk, id: params.id });
  const item = props.item || queriedItem;
  const [updateProperty] = useUpdatePropertyMutation();

  const [editingReference, setEditingReference] = useState(null);
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [sortedReferences, setSortedReferences] = useState([]);
  const [sortedBy, setSortedBy] = useState('name');

  useEffect(() => {
    if (item && item.bibliography) {
      let _newreferences = Array.isArray(item.bibliography) ? [...item.bibliography] : [];
      _newreferences = _newreferences.filter(ref => ref != null); // Remove any null/undefined entries
      
      _newreferences.sort((a, b) => {
        let aValue, bValue;
        if (sortedBy === 'author') {
          aValue = a.author || a.title || '';
          bValue = b.author || b.title || '';
        } else {
          aValue = a[sortedBy] || '';
          bValue = b[sortedBy] || '';
        }
        if (aValue < bValue) return -1;
        if (aValue > bValue) return 1;
        return 0;
      });
      setSortedReferences(_newreferences);
    } else {
      setSortedReferences([]);
    }
  }, [item, sortedBy]);

  const handlePrint = () => {
    if (!item) return;
    const projectName = item?.name || 'References';
    const referencesHtml = formatReferencesHtml(sortedReferences);
    
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;
    
    printWindow.document.write(`
      <html><head><title>${projectName}</title>
      <style>
        @page { margin: 0; }
        body { font-family: "Times New Roman", Times, serif; font-size: 12pt; margin: 1in; }
        h3 { margin-bottom: 10px; }
        p { font-size: 12pt; }
      </style>
      </head>
      <body>
        <h3>${projectName}</h3>
        <div>${referencesHtml}</div>

      </body>
      </html>`);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
    }, 1000);
  };

  const formatReferencesHtml = (references) => {
    if (!references) return '';
    let refs = '<h3>References</h3>';
    references.forEach((ref) => {
      refs += `<p>${formatCitationHtml(ref)}</p>`;
    });
    return refs;
  };

  const formatCitationHtml = (ref) => {
    return `
      ${ref.author ? `<span>${ref.author}. </span>` : ''}
      ${ref.subtitle ? `<span>${ref.subtitle}. </span>` : ''}
      ${ref.title ? `<i>${ref.title}. </i>` : ''}
      ${ref.publisher ? `<span>${ref.publisher}, </span>` : ''}
      ${ref.when ? `<span>${ref.when} </span>` : ''}
      ${ref.pages ? `<span>pp: ${ref.pages} </span>` : ''}
      ${ref.url ? `<a href="${ref.url}" target="_blank">${ref.url}</a>` : ''}
      ${ref.timestamp ? `<span>Timestamp: ${ref.timestamp}</span>` : ''}
    `;
  };

  const ReferenceForm = ({ reference, onSave, onCancel }) => {
    const [formData, setFormData] = useState(
      reference || {
        name: '',
        description: '',
        type: 'book',
        author: '',
        title: '',
        subtitle: '',
        publisher: '',
        when: '',
        pages: '',
        url: '',
        timestamp: ''
      }
    );

    const handleSubmit = (e) => {
      e.preventDefault();
      onSave(formData);
    };

    return (
      <form onSubmit={handleSubmit}>
        <Box sx={{ mb: 2 }}>
          <Typography>Name</Typography>
          <TextField value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} fullWidth size="small" />
        </Box>
        <Box sx={{ mb: 2 }}>
          <Typography>Description</Typography>
          <TextareaAutosize
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            minRows={3}
            style={{ width: '100%' }}
          />
        </Box>
        <Box sx={{ mb: 2 }}>
          <Typography>Type</Typography>
          <Select value={formData.type} onChange={(e) => setFormData({ ...formData, type: e.target.value })} fullWidth size="small">
            <MenuItem value="book">Book</MenuItem>
            <MenuItem value="article">Article</MenuItem>
            <MenuItem value="website">Website</MenuItem>
            <MenuItem value="youtube">YouTube</MenuItem>
          </Select>
        </Box>
        <Box sx={{ mb: 2 }}>
          <Typography>Author</Typography>
          <TextField
            value={formData.author}
            onChange={(e) => setFormData({ ...formData, author: e.target.value })}
            fullWidth
            size="small"
          />
        </Box>
        <Box sx={{ mb: 2 }}>
          <Typography>Title</Typography>
          <TextField value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} fullWidth size="small" />
        </Box>
        <Box sx={{ mb: 2 }}>
          <Typography>URL</Typography>
          <TextField value={formData.url} onChange={(e) => setFormData({ ...formData, url: e.target.value })} fullWidth size="small" />
        </Box>
        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <Box sx={{ flex: 1 }}>
            <Typography>Publisher</Typography>
            <TextField
              value={formData.publisher}
              onChange={(e) => setFormData({ ...formData, publisher: e.target.value })}
              fullWidth
              size="small"
            />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography>When</Typography>
            <TextField value={formData.when} onChange={(e) => setFormData({ ...formData, when: e.target.value })} fullWidth size="small" />
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <Box sx={{ flex: 1 }}>
            <Typography>Pages</Typography>
            <TextField
              value={formData.pages}
              onChange={(e) => setFormData({ ...formData, pages: e.target.value })}
              fullWidth
              size="small"
            />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography>Timestamp</Typography>
            <TextField
              value={formData.timestamp}
              onChange={(e) => setFormData({ ...formData, timestamp: e.target.value })}
              fullWidth
              size="small"
            />
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button type="submit" variant="contained" color="primary">
            Save
          </Button>
          <Button onClick={onCancel} variant="outlined" color="secondary">
            Cancel
          </Button>
        </Box>
      </form>
    );
  };

  const handleEdit = (reference) => {
    setEditingReference(reference);
    setIsAddingNew(false);
  };

  const handleSave = (updatedReference) => {
    let _newreferences = item.bibliography ? [...item.bibliography] : [];
    if (isAddingNew) {
      const newReference = { ...updatedReference, id: generateUniqueId(6) };
      _newreferences.push(newReference);
    } else {
      _newreferences = _newreferences.map((ref) => (ref.id === editingReference.id ? { ...updatedReference, id: ref.id } : ref));
    }
    updateProperty({
      label: item.label,
      pk: item.pk,
      id: item.id,
      name: 'bibliography',
      value: _newreferences
    });
    setEditingReference(null);
    setIsAddingNew(false);
  };

  const handleAddNew = () => {
    setIsAddingNew(true);
    setEditingReference(null);
  };

  const handleDelete = (reference) => {
    const _newreferences = item.bibliography.filter((c) => c.id !== reference.id);
    updateProperty({
      label: item.label,
      pk: item.pk,
      id: item.id,
      name: 'bibliography',
      value: _newreferences
    });
  };

  const formatCitation = (citation) => {
    return (
      <span>
        {citation.author ? <span>{citation.author}. </span> : ''}
        {citation.subtitle ? <span>{citation.subtitle}. </span> : ''}
        {citation.title ? <i>{citation.title}. </i> : ''}
        {citation.publisher ? <span>{citation.publisher}, </span> : ''}
        {citation.when ? <span>{citation.when} </span> : ''}
        {citation.pages ? <span>pp: {citation.pages} </span> : ''}
        {citation.url ? (
          <span>
            <a href={citation.url} target="_blank" rel="noreferrer">
              {citation.url}
            </a>{' '}
          </span>
        ) : (
          ''
        )}
        {citation.timestamp ? <span>Timestamp: {citation.timestamp}</span> : ''}
      </span>
    );
  };

  return isLoading ? (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
      <CircularProgress />
    </Box>
  ) : (
    <Box>
      <ProjectBreadcrumbs item={item} name="Bibliography" />
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4">{item.name}</Typography>
        <Button onClick={handlePrint} variant="contained" color="primary">
          Print
        </Button>
      </Box>
      <Box sx={{ mb: 2 }}>
        <Typography>Sort by:</Typography>
        <Select value={sortedBy} onChange={(e) => setSortedBy(e.target.value)} fullWidth size="small">
          <MenuItem value="name">Name</MenuItem>
          <MenuItem value="type">Type</MenuItem>
          <MenuItem value="author">Author</MenuItem>
          <MenuItem value="title">Title</MenuItem>
        </Select>
      </Box>
      <Divider sx={{ mb: 2 }} />
      <Box>
        {sortedReferences.map((step, i) => (
          <Card key={i} sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, padding: 2 }}>
            <Box>
              {step.name && (
                <Typography variant="h6" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                  {step.type === 'book' && <MenuBookIcon color="primary" />}
                  {step.type === 'article' && <ArticleIcon color="primary" />}
                  {step.type === 'website' && <LanguageIcon color="primary" />}
                  {step.type === 'youtube' && <SmartDisplayIcon color="error" />}
                  {step.name}
                </Typography>
              )}
              {step.description && (
                <Typography variant="body2" sx={{ mb: 1 }}>
                  {step.description}
                </Typography>
              )}
              <Typography variant="body2">{formatCitation(step)}</Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <IconButton color="primary" onClick={() => handleEdit(step)} size="small">
                <EditIcon />
              </IconButton>
              <IconButton color="error" onClick={() => handleDelete(step)} size="small">
                <DeleteIcon />
              </IconButton>
            </Box>
          </Card>
        ))}
      </Box>
      <Button onClick={handleAddNew} variant="contained" color="primary" sx={{ mt: 2 }}>
        Add New Reference
      </Button>
      {(isAddingNew || editingReference) && (
        <Card sx={{ mt: 2, padding: 2 }}>
          <ReferenceForm
            reference={editingReference}
            onSave={handleSave}
            onCancel={() => {
              setEditingReference(null);
              setIsAddingNew(false);
            }}
          />
        </Card>
      )}
    </Box>
  );
};

export default Bibliography;
